import React, { useState } from 'react';
import { ConfigDocument, DigitalDestination, TestResult } from '../../types';
import { testSharePointConnection, testSMBConnection } from '../../services/configApi';

interface DigitalDestinationsTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  loading: boolean;
}

interface FormData {
  name: string;
  type: 'sharepoint' | 'smb';
  credentialSetName: string;
  // SharePoint fields
  siteUrl: string;
  documentLibrary: string;
  siteFolder: string;
  // SMB fields
  uncPath: string;
}

interface ValidationErrors {
  name?: string;
  type?: string;
  credentialSetName?: string;
  siteUrl?: string;
  documentLibrary?: string;
  siteFolder?: string;
  uncPath?: string;
}

interface ConnectionTestState {
  loading: boolean;
  result: TestResult | null;
}

const EMPTY_FORM: FormData = {
  name: '',
  type: 'sharepoint',
  credentialSetName: '',
  siteUrl: '',
  documentLibrary: '',
  siteFolder: '',
  uncPath: '',
};

const DigitalDestinationsTab: React.FC<DigitalDestinationsTabProps> = ({ config, onSave, loading }) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [testStates, setTestStates] = useState<Record<string, ConnectionTestState>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Get credential set names filtered by matching type
  const getFilteredCredentialSets = (type: 'sharepoint' | 'smb'): string[] => {
    if (!config) return [];
    return Object.entries(config.credentialSets)
      .filter(([, cred]) => cred.type === type)
      .map(([name]) => name)
      .sort((a, b) => a.localeCompare(b));
  };

  const destinations: [string, DigitalDestination][] = config
    ? Object.entries(config.digitalDestinations || {}).sort(([a], [b]) => a.localeCompare(b))
    : [];

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Destination name is required';
    } else if (
      editing === 'new' &&
      config?.digitalDestinations &&
      formData.name.trim() in config.digitalDestinations
    ) {
      newErrors.name = 'A destination with this name already exists';
    }

    if (!formData.credentialSetName) {
      newErrors.credentialSetName = 'Credential set is required';
    }

    if (formData.type === 'sharepoint') {
      if (!formData.siteUrl.trim()) newErrors.siteUrl = 'Site URL is required';
      if (!formData.documentLibrary.trim()) newErrors.documentLibrary = 'Document library is required';
      if (!formData.siteFolder.trim()) newErrors.siteFolder = 'Site folder is required';
    } else {
      if (!formData.uncPath.trim()) newErrors.uncPath = 'UNC path is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddNew = () => {
    setEditing('new');
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleEdit = (key: string) => {
    const dest = config?.digitalDestinations[key];
    if (!dest) return;
    setEditing(key);
    setFormData({
      name: key,
      type: dest.type,
      credentialSetName: dest.credentialSetName,
      siteUrl: dest.siteUrl || '',
      documentLibrary: dest.documentLibrary || '',
      siteFolder: dest.siteFolder || '',
      uncPath: dest.uncPath || '',
    });
    setErrors({});
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate() || !config) return;

    setSaving(true);
    try {
      const updatedDestinations = { ...(config.digitalDestinations || {}) };

      if (editing !== 'new' && editing !== formData.name.trim()) {
        delete updatedDestinations[editing!];
      }

      const dest: DigitalDestination = {
        type: formData.type,
        credentialSetName: formData.credentialSetName,
      };

      if (formData.type === 'sharepoint') {
        dest.siteUrl = formData.siteUrl.trim();
        dest.documentLibrary = formData.documentLibrary.trim();
        dest.siteFolder = formData.siteFolder.trim();
      } else {
        dest.uncPath = formData.uncPath.trim();
      }

      updatedDestinations[formData.name.trim()] = dest;

      await onSave({
        ...config,
        digitalDestinations: updatedDestinations,
      });

      setEditing(null);
      setFormData({ ...EMPTY_FORM });
      setErrors({});
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!config) return;

    const updatedDestinations = { ...(config.digitalDestinations || {}) };
    delete updatedDestinations[key];

    setSaving(true);
    try {
      await onSave({
        ...config,
        digitalDestinations: updatedDestinations,
      });
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (key: string) => {
    const dest = config?.digitalDestinations[key];
    if (!dest) return;

    setTestStates((prev) => ({
      ...prev,
      [key]: { loading: true, result: null },
    }));

    try {
      let result: TestResult;
      if (dest.type === 'sharepoint') {
        result = await testSharePointConnection({
          credentialSetName: dest.credentialSetName,
          siteUrl: dest.siteUrl!,
          documentLibrary: dest.documentLibrary!,
        });
      } else {
        result = await testSMBConnection({
          uncPath: dest.uncPath!,
          credentialSetName: dest.credentialSetName,
        });
      }
      setTestStates((prev) => ({
        ...prev,
        [key]: { loading: false, result },
      }));
    } catch (err: any) {
      setTestStates((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          result: { success: false, message: err.message || 'Connection test failed' },
        },
      }));
    }
  };

  if (loading && !config) {
    return <div className="text-gray-500">Loading configuration...</div>;
  }

  const renderFormFields = () => {
    const filteredCreds = getFilteredCredentialSets(formData.type);
    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as 'sharepoint' | 'smb', credentialSetName: '' })}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="sharepoint">SharePoint</option>
            <option value="smb">SMB</option>
          </select>
        </div>
        {formData.type === 'sharepoint' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Site URL</label>
              <input type="text" value={formData.siteUrl} onChange={(e) => setFormData({ ...formData, siteUrl: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.siteUrl ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="https://tenant.sharepoint.com/sites/SiteName" />
              {errors.siteUrl && <p className="mt-1 text-sm text-red-600">{errors.siteUrl}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Document Library</label>
              <input type="text" value={formData.documentLibrary} onChange={(e) => setFormData({ ...formData, documentLibrary: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.documentLibrary ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Digital Output" />
              {errors.documentLibrary && <p className="mt-1 text-sm text-red-600">{errors.documentLibrary}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Site Folder</label>
              <input type="text" value={formData.siteFolder} onChange={(e) => setFormData({ ...formData, siteFolder: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.siteFolder ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="cle" />
              {errors.siteFolder && <p className="mt-1 text-sm text-red-600">{errors.siteFolder}</p>}
            </div>
          </>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700">UNC Path</label>
            <input type="text" value={formData.uncPath} onChange={(e) => setFormData({ ...formData, uncPath: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.uncPath ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="//10.1.4.44/Digital-Output" />
            {errors.uncPath && <p className="mt-1 text-sm text-red-600">{errors.uncPath}</p>}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700">Credential Set</label>
          <select value={formData.credentialSetName} onChange={(e) => setFormData({ ...formData, credentialSetName: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.credentialSetName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}>
            <option value="">Select a credential set...</option>
            {filteredCreds.map((name) => (<option key={name} value={name}>{name}</option>))}
          </select>
          {errors.credentialSetName && <p className="mt-1 text-sm text-red-600">{errors.credentialSetName}</p>}
          {filteredCreds.length === 0 && (
            <p className="mt-1 text-sm text-yellow-600">No {formData.type} credential sets available. Create one in the Credentials tab first.</p>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Add New button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Digital Destinations</h3>
        {editing === null && (
          <button
            type="button"
            onClick={handleAddNew}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New
          </button>
        )}
      </div>

      {/* Add New Form */}
      {editing === 'new' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Add New Digital Destination</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dest-name" className="block text-sm font-medium text-gray-700">Destination Name (Site Code)</label>
              <input id="dest-name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="CLE" />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            {renderFormFields()}
          </div>
          <div className="mt-4 flex space-x-3">
            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Destinations List */}
      {destinations.length === 0 && editing === null ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No digital destinations configured. Click "Add New" to create one.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Site</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credential Set</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {destinations.map(([key, dest]) => {
                const testState = testStates[key];
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{key}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${dest.type === 'sharepoint' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {dest.type === 'sharepoint' ? 'SharePoint' : 'SMB'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dest.type === 'sharepoint' ? `${dest.siteUrl} / ${dest.documentLibrary} / ${dest.siteFolder}` : dest.uncPath}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dest.credentialSetName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                        <button type="button" onClick={() => handleEdit(key)} disabled={editing !== null} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Edit</button>
                        <button type="button" onClick={() => setDeleteConfirm(key)} disabled={editing !== null} className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">Delete</button>
                        <button type="button" onClick={() => handleTest(key)} disabled={editing !== null || testState?.loading} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{testState?.loading ? 'Testing...' : 'Test'}</button>
                      </td>
                    </tr>
                    {/* Inline edit form */}
                    {editing === key && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-blue-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {renderFormFields()}
                          </div>
                          <div className="mt-3 flex space-x-3">
                            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
                            <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Test result banner */}
                    {testState?.result && (
                      <tr>
                        <td colSpan={5} className="px-6 py-0 pb-3">
                          <TestResultBanner result={testState.result} />
                        </td>
                      </tr>
                    )}
                    {/* Delete confirmation */}
                    {deleteConfirm === key && (
                      <tr>
                        <td colSpan={5} className="px-6 py-3">
                          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" role="alert">
                            <p className="text-sm">Are you sure you want to delete <span className="font-medium">{key}</span>?</p>
                            <div className="mt-2 flex space-x-3">
                              <button type="button" onClick={() => handleDelete(key)} disabled={saving} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{saving ? 'Deleting...' : 'Confirm Delete'}</button>
                              <button type="button" onClick={() => setDeleteConfirm(null)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TestResultBanner: React.FC<{ result: TestResult }> = ({ result }) => (
  <div
    className={`p-3 rounded-md flex items-center space-x-2 ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
    role="alert"
  >
    <span className="text-lg">{result.success ? '✓' : '✗'}</span>
    <span className="text-sm">{result.message || (result.success ? 'Connection successful' : 'Connection failed')}</span>
  </div>
);

export default DigitalDestinationsTab;
